import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Bus
} from "lucide-react";
import {
  Button,
  IconButton,
  Input,
  Select,
  Table,
  Badge,
  EmptyState,
  PageHeader,
  Tooltip
} from "../../components/ui";
import VehicleModal from "../../components/transport/VehicleModal";
import {
  getAllVehicles,
  getAllVehiclesPortal,
  deleteVehicle,
  deleteVehiclePortal,
  getAcademicYears,
  getAcademicYearsPortal
} from "../../api/transport/transportApi";
import {
  useToast
} from "../../context/ToastContext";

const VehicleList = ({ isPortal = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [academicYearId, setAcademicYearId] = useState("");
  const [yearOptions, setYearOptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = isPortal ? await getAcademicYearsPortal() : await getAcademicYears();
        const list = res.data || res || [];
        const opts = list.map((y) => ({
          value: y._id,
          label: y.yearName || y.name || String(y._id),
        }));
        setYearOptions(opts);
        const active = list.find((y) => y.isActive);
        if (active) setAcademicYearId(active._id);
        else if (list[0]) setAcademicYearId(list[0]._id);
      } catch {
        setYearOptions([]);
      }
    })();
  }, [isPortal]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (academicYearId) params.academicYearId = academicYearId;
      const res = isPortal
        ? await getAllVehiclesPortal(params)
        : await getAllVehicles(params);
      setRows(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load vehicles");
    } finally {
      setLoading(false);
    }
  }, [isPortal, academicYearId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vehicle? Active assignments will block deletion.")) return;
    setDeletingId(id);
    try {
      if (isPortal) await deleteVehiclePortal(id);
      else await deleteVehicle(id);
      toast.success("Vehicle deleted.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (r.name || "").toLowerCase().includes(q) ||
      (r.vehicleNumber || "").toLowerCase().includes(q) ||
      (r.driverName || "").toLowerCase().includes(q) ||
      (r.vehicleType || "").toLowerCase().includes(q) ||
      (Array.isArray(r.pincodes) ? r.pincodes.join(" ") : "").includes(q)
    );
  });

  const columns = [
    {
      key: "name",
      header: "Vehicle",
      render: (row) => (
        <div>
          <div className="font-medium text-neutral-900 dark:text-neutral-100">{row.name}</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{row.vehicleNumber}</div>
        </div>
      ),
    },
    {
      key: "vehicleType",
      header: "Type",
      render: (row) => (
        <Badge variant="primary">{(row.vehicleType || "").replace(/_/g, " ")}</Badge>
      ),
    },
    {
      key: "pincodes",
      header: "Pincodes",
      render: (row) =>
        Array.isArray(row.pincodes) && row.pincodes.length
          ? row.pincodes.join(", ")
          : "—",
    },
    {
      key: "driverName",
      header: "Driver",
      render: (row) => (
        <div>
          <div>{row.driverName || "—"}</div>
          {row.driverPhone ? (
            <div className="text-xs text-neutral-500 dark:text-neutral-400">{row.driverPhone}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: "capacity",
      header: "Seats",
      render: (row) => {
        if (row.occupiedSeats != null) {
          const full = row.isFull;
          return (
            <Badge variant={full ? "danger" : "success"}>
              {row.occupiedSeats}/{row.capacity}
              {full ? " Full" : ""}
            </Badge>
          );
        }
        return <span>{row.capacity}</span>;
      },
    },
    {
      key: "isActive",
      header: "Status",
      render: (row) => (
        <Badge variant={row.isActive ? "success" : "default"}>
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip content="Edit">
            <IconButton
              aria-label="Edit vehicle"
              variant="ghost"
              onClick={() => {
                setEditData(row);
                setModalOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </IconButton>
          </Tooltip>
          <Tooltip content="Delete">
            <IconButton
              aria-label="Delete vehicle"
              variant="ghost"
              disabled={deletingId === row._id}
              onClick={() => handleDelete(row._id)}
            >
              <Trash2 className="h-4 w-4 text-danger-500" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <PageHeader
        title="Transport Vehicles"
        description="Configure buses/vans, capacity, routes and pincodes served"
        actions={
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setEditData(null);
              setModalOpen(true);
            }}
          >
            Add vehicle
          </Button>
        }
      />

      
      

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Search name, number, driver, pincode..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
        <Select
          options={yearOptions}
          value={academicYearId}
          onChange={setAcademicYearId}
          placeholder="Academic year (for occupancy)"
          className="max-w-xs"
        />
      </div>

      <Table
        columns={columns}
        data={filtered}
        isLoading={loading}
        emptyState={
          <EmptyState
            icon={Bus}
            title="No vehicles yet"
            description="Add vehicles with capacity and pincodes to start assigning students."
            action={
              <Button
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  setEditData(null);
                  setModalOpen(true);
                }}
              >
                Add vehicle
              </Button>
            }
          />
        }
      />

      <VehicleModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          toast.success(editData ? "Vehicle updated." : "Vehicle created.");
          fetchData();
        }}
        editData={editData}
        isPortal={isPortal}
      />
    </div>
  );
};

export default VehicleList;
